import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { timeTrackingApi, TimeEntry } from '../../api/endpoints/timeTrackingApi';
import { format } from 'date-fns/format';

interface TimeEntriesProps {
  taskId: string;
}

export const TimeEntries: React.FC<TimeEntriesProps> = ({ taskId }) => {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await timeTrackingApi.getTaskEntries(taskId);
      setEntries(data);
    } catch (error) {
      console.error('Failed to load time entries:', error);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const loadTotal = useCallback(async () => {
    try {
      const data = await timeTrackingApi.getTaskTotal(taskId);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load total time:', error);
    }
  }, [taskId]);

  useEffect(() => {
    loadEntries();
    loadTotal();
  }, [loadEntries, loadTotal]);

  const handleDelete = async (entryId: string) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) {
      return;
    }

    try {
      await timeTrackingApi.deleteEntry(entryId);
      loadEntries();
      loadTotal();
    } catch (error) {
      console.error('Failed to delete time entry:', error);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);

    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatTime = (dateString: string): string => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Time Entries</Typography>
        <Chip
          label={`Total: ${formatDuration(total)}`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {entries.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
          No time entries yet
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.user?.name || 'Unknown'}</TableCell>
                  <TableCell>
                    <strong>{formatDuration(entry.duration || 0)}</strong>
                  </TableCell>
                  <TableCell>{formatTime(entry.startTime)}</TableCell>
                  <TableCell>
                    {entry.endTime ? (
                      formatTime(entry.endTime)
                    ) : (
                      <Chip label="Running" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{entry.description || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(entry.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default TimeEntries;
